import { Injectable, NgZone } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { MsalService } from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class SignalrRefreshService {
  private hubConnection: HubConnection | null = null;
  private registeredEvents: string[] = [];
  private readonly refreshSubject = new Subject<string>();

  refresh$ = this.refreshSubject.asObservable();

  constructor(private zone: NgZone, private msalService: MsalService) {}

  private async getApiAccessToken(): Promise<string | undefined> {
    const account = this.msalService.instance.getActiveAccount() ?? this.msalService.instance.getAllAccounts()[0];
    if (!account) {
      return undefined;
    }

    try {
      const response = await this.msalService.instance.acquireTokenSilent({
        account,
        scopes: environment.apiConfig.scopes
      });
      return response.accessToken;
    } catch (error) {
      console.warn('SignalR token acquisition failed, continuing without token.', error);
      return undefined;
    }
  }

  async startConnection(hubUrl: string, refreshEvents: string[] = ['AppraisalUpdated']): Promise<boolean> {
    if (typeof window === 'undefined' || !hubUrl) {
      return false;
    }

    if (this.hubConnection?.state === 'Connected') {
      return true;
    }

    try {
      await this.stopConnection();

      this.hubConnection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          withCredentials: false,
          accessTokenFactory: async () => (await this.getApiAccessToken()) ?? ''
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.Information)
        .build();

      this.registeredEvents = refreshEvents.length > 0 ? refreshEvents : ['AppraisalUpdated'];

      this.hubConnection.onreconnecting(error => {
        console.warn('SignalR reconnecting...', error);
      });

      this.hubConnection.onreconnected(connectionId => {
        console.info('SignalR reconnected. ConnectionId:', connectionId);
      });

      this.hubConnection.onclose(error => {
        console.warn('SignalR connection closed.', error);
      });

      this.registeredEvents.forEach(eventName => {
        this.hubConnection?.on(eventName, (payload?: unknown) => {
          console.info('SignalR refresh event received:', eventName, payload);
          this.zone.run(() => this.refreshSubject.next(eventName));
        });
      });

      await this.hubConnection.start();
      console.info('SignalR connection established:', hubUrl);
      return true;
    } catch (error) {
      console.error('SignalR connection failed:', error);
      return false;
    }
  }

  async stopConnection(): Promise<void> {
    if (!this.hubConnection) {
      return;
    }

    try {
      this.registeredEvents.forEach(eventName => {
        this.hubConnection?.off(eventName);
      });
      this.registeredEvents = [];
      await this.hubConnection.stop();
    } catch (error) {
      console.error('SignalR disconnection failed:', error);
    } finally {
      this.hubConnection = null;
    }
  }
}
